import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown } from 'react-icons/fa';
import './FAQ.css';

const faqData = [
  {
    category: 'Admissions',
    questions: [
      {
        q: 'How do I apply for admission to Sri Vani schools?',
        a: 'You can apply online through our admissions portal at opus-svect.com or visit the nearest campus office. Applications are accepted throughout the year, subject to seat availability.',
      },
      {
        q: 'What is the age criteria for admission to Pre-Primary?',
        a: 'Children must be at least 3 years of age as of June 1st of the academic year for LKG admission. For Nursery, the minimum age is 2.5 years.',
      },
      {
        q: 'What documents are required for admission?',
        a: 'Required documents include: birth certificate, previous school transfer certificate (if applicable), Aadhaar card of child and parents, passport-size photographs, and proof of residence.',
      },
      {
        q: 'Is there an entrance test for admission?',
        a: 'For Classes I to IX, an assessment may be conducted to understand the child\'s learning level. There is no entrance test for Pre-Primary classes.',
      },
      {
        q: 'Are mid-year admissions available?',
        a: 'Mid-year admissions are considered subject to seat availability. Please contact the respective campus admissions office for current availability.',
      },
    ],
  },
  {
    category: 'Curriculum & Academics',
    questions: [
      {
        q: 'Which curricula does Sri Vani offer?',
        a: 'Sri Vani offers State Syllabus (Karnataka), CBSE, ICSE, and Cambridge International (IGCSE/A-Levels) across its campuses. Availability varies by campus.',
      },
      {
        q: 'What grades are offered across campuses?',
        a: 'We offer education from Pre-Primary (Nursery/LKG/UKG) through Class XII and PU College (Science & Commerce streams) at our Machohalli campus.',
      },
      {
        q: 'Does the school follow a co-educational system?',
        a: 'Yes, Sri Vani schools are co-educational and welcome both boys and girls across all campuses.',
      },
      {
        q: 'Are there any special programmes or extracurricular activities?',
        a: 'Yes, we offer a wide range of programmes including Robotics, Coding, Music, Dance, Drama, Visual Arts, multiple sports disciplines, and our unique Ethnomusicology programme through the Sampradaya Academy.',
      },
    ],
  },
  {
    category: 'Fees & Finance',
    questions: [
      {
        q: 'How can I get details on school fees?',
        a: 'Fee details are provided at the time of admission or can be obtained by contacting the respective campus office. Fees vary by campus, curriculum, and grade.',
      },
      {
        q: 'Are there any scholarships or fee concessions available?',
        a: 'Sri Vani offers merit-based and need-based concessions. Please contact the admissions office or email ask@srivani.org for details.',
      },
      {
        q: 'What are the payment modes accepted?',
        a: 'Fees can be paid via online bank transfer, cheque, demand draft, or through our online payment portal. Cash payments are accepted at the campus office.',
      },
    ],
  },
  {
    category: 'Transport & Safety',
    questions: [
      {
        q: 'Does the school provide transport facility?',
        a: 'Yes, Sri Vani operates a fleet of school buses covering 50+ routes across Bangalore. All buses are equipped with GPS tracking and have trained female attendants on board.',
      },
      {
        q: 'How do I enrol for the school bus service?',
        a: 'Transport enrolment forms are available at the campus office. Please contact the transport coordinator of your nearest campus for route details and availability.',
      },
      {
        q: 'What safety measures are in place on campus?',
        a: 'All campuses have CCTV surveillance, trained security personnel, a medical room with trained staff, and strict visitor protocols. Student safety is our top priority.',
      },
    ],
  },
  {
    category: 'Campus & Facilities',
    questions: [
      {
        q: 'Which campus should I choose?',
        a: 'We recommend choosing the campus closest to your residence. Each campus offers slightly different curricula and grade ranges — visit our Campuses page for a full comparison.',
      },
      {
        q: 'Does the Machohalli campus have hostel facilities?',
        a: 'Yes, the Machohalli (Hanumavana) campus offers hostel facilities for students. Please contact the campus directly at mgdr@srivani.org for availability and details.',
      },
      {
        q: 'Are the school premises accessible for differently-abled students?',
        a: 'We are progressively upgrading our facilities to be more inclusive. Please contact the specific campus to understand the current accessibility provisions.',
      },
    ],
  },
];

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={`faq-item ${open ? 'open' : ''}`}>
      <button className="faq-question" onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <FaChevronDown className={`faq-chevron ${open ? 'open' : ''}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="faq-answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            <p>{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', ...faqData.map((f) => f.category)];

  const filtered = activeCategory === 'All'
    ? faqData
    : faqData.filter((f) => f.category === activeCategory);

  return (
    <div className="faq-page">
      <section className="page-header">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1>Frequently Asked Questions</h1>
            <p>Find answers to common questions about Sri Vani Education</p>
          </motion.div>
        </div>
      </section>

      <section className="faq-section section">
        <div className="container">
          {/* Category filter */}
          <div className="faq-categories">
            {categories.map((cat) => (
              <button
                key={cat}
                className={`faq-category-btn ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Questions */}
          <div className="faq-list">
            {filtered.map((section) => (
              <motion.div
                key={section.category}
                className="faq-group"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <h3 className="faq-group-title">{section.category}</h3>
                {section.questions.map((item, idx) => (
                  <FAQItem key={idx} question={item.q} answer={item.a} />
                ))}
              </motion.div>
            ))}
          </div>

          {/* Still have questions */}
          <motion.div
            className="faq-cta"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3>Still have questions?</h3>
            <p>Our team is happy to help. Reach us at <a href="mailto:ask@srivani.org">ask@srivani.org</a> or call <a href="tel:+918023322239">+91-80-2332 2239</a></p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
